import {
  CloseOutlined,
  PauseOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import { Popover, Progress } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { IoIosCopy, IoMdDownload } from "react-icons/io";
import { useInfoUser } from "../../store/UserStore";
import { getFileIcon } from "../../utils/SendFile";
import {
  handleCopy,
  isSupportedFileType,
  standardizeVolume,
} from "../../utils/Utils";
import { RESOURCE } from "../../api/apiConstant";
import { useGlobalDownloader } from "../../context/global/DownloadProvider";

const getMinioUrl = (path) => {
  if (!path) return null;

  try {
    return new URL(path).href;
  } catch {
    const baseUrl = new URL(
      process.env.REACT_APP_FILE_STORE || window.location.origin,
      window.location.origin,
    );
    const basePath = baseUrl.pathname.replace(/\/+$/, "");
    const normalizedPath = String(path).replace(/^\/+/, "");
    const basePathWithoutLeadingSlash = basePath.replace(/^\/+/, "");
    const isBasePathIncluded =
      basePathWithoutLeadingSlash &&
      (normalizedPath === basePathWithoutLeadingSlash ||
        normalizedPath.startsWith(`${basePathWithoutLeadingSlash}/`));

    return new URL(
      isBasePathIncluded
        ? `/${normalizedPath}`
        : `${basePath}/${normalizedPath}`.replace(/^\/+/, "/"),
      baseUrl.origin,
    ).href;
  }
};

const VersionPreviewFile = ({ file }) => {
  const { languageMap } = useInfoUser();

  const {
    downloads,
    startDownload,
    cancelDownload,
    pauseDownload,
    resumeDownload,
    DOWNLOAD_STATUS,
  } = useGlobalDownloader();

  const [fileState, setFileState] = useState({});
  const minioUrl = getMinioUrl(file?.mainPath);
  const downloadPath =
    minioUrl ||
    (file?.resourceId
      ? `${(process.env.REACT_APP_AUTHENTICATION_API_URL || "").replace(
        /\/+$/,
      )}/${RESOURCE}/public/${encodeURIComponent(file.resourceId)}`
      : null);
  const fileNameToSave =
    file?.originalName || file?.fileName || "Willo-video";

  const handleResumeDownload = (e) => {
    e.stopPropagation();
    resumeDownload(downloadPath, fileNameToSave);
  };

  const handlePauseDownload = (e) => {
    e.stopPropagation();
    pauseDownload(downloadPath);
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    startDownload(downloadPath, fileNameToSave, 0);
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    cancelDownload(downloadPath);
  };

  const renderDownloadButton = useCallback(() => {
    switch (fileState?.status) {
      case DOWNLOAD_STATUS.PAUSED:
        return (
          <div className="flex gap-2">
            <button onClick={handleResumeDownload}>
              <PlayCircleOutlined
                style={{ fontSize: "20px", color: "green" }}
              />
            </button>
            <button onClick={handleCancel}>
              <CloseOutlined style={{ fontSize: "20px", color: "red" }} />
            </button>
          </div>
        );
      case DOWNLOAD_STATUS.DOWNLOADING:
        return (
          <>
            <button onClick={handlePauseDownload}>
              <PauseOutlined style={{ fontSize: "20px" }} />
            </button>
            <button onClick={handleCancel}>
              <CloseOutlined style={{ fontSize: "20px" }} />
            </button>
          </>
        );

      case DOWNLOAD_STATUS.SUCCESS:
        return (
          <button onClick={handleDownload}>
            <IoMdDownload style={{ fontSize: "20px", color: "green" }} />
          </button>
        );
      case DOWNLOAD_STATUS.ERROR:
        return (
          <div className="flex gap-2">
            <button onClick={handleCancel}>
              <CloseOutlined style={{ fontSize: "20px", color: "red" }} />
            </button>
          </div>
        );
      default:
        return (
          <button onClick={handleDownload}>
            <IoMdDownload style={{ fontSize: "20px", color: "green" }} />
          </button>
        );
    }
  }, [fileState?.status, downloadPath, fileNameToSave]);

  const subOriginalName = () => {
    const maxLength = 15;
    let newOriginalName;

    if (file?.originalName?.trim()?.length > maxLength + 7) {
      newOriginalName =
        file?.originalName?.slice(0, maxLength) +
        "..." +
        file?.originalName?.slice(-7);
    } else {
      newOriginalName = file?.originalName;
    }

    return newOriginalName?.trim() || "Unknown.file";
  };

  const fileIcon = useMemo(() => {
    return getFileIcon(file?.originalName);
  }, [file?.originalName]);

  const progressRate = useMemo(() => {
    return (
      <div className="flex gap-[2px]">
        <div>
          {fileState?.progress
            ? standardizeVolume((fileState?.progress * file?.volume) / 100)
            : 0}
        </div>
        <div>/</div>
        <div>{standardizeVolume(file?.volume)}</div>
      </div>
    );
  }, [fileState?.progress]);

  useEffect(() => {
    setFileState(downloads[downloadPath]);
  }, [downloads, downloadPath]);

  return (
    <>
      <div className="file-container cursor-pointer w-[320px]">
        <div className="max-h-[300px] flex flex-row my-1 items-center">
          <div>{fileIcon}</div>
          <div className="flex flex-col w-full">
            <Popover content={file?.originalName}>
              <div className="pr-[10px] text-[12px]">{subOriginalName()}</div>
            </Popover>

            <div className="flex gap-[10px] justify-between me-2 file-info">
              <div className={`text-[grey] text-[12px]`}>
                <span
                  className={
                    isSupportedFileType(file?.originalName) ? "volume" : ""
                  }
                >
                  {file?.volume && standardizeVolume(file?.volume)}
                </span>
                {isSupportedFileType(file?.originalName) && (
                  <span className="preview-text">
                    {languageMap?.["preview.click"] ?? "Click to preview"}
                  </span>
                )}
              </div>

              {renderDownloadButton()}
              <button
                onClick={() =>
                  handleCopy(
                    minioUrl ||
                    `${process.env.REACT_APP_CURRENT_WEB}/download/${file?.resourceId}`,
                  )
                }
              >
                <IoIosCopy size={17} color={"#2a56b9"} />
              </button>
            </div>
          </div>
        </div>

        {[DOWNLOAD_STATUS.DOWNLOADING].includes(fileState?.status) && (
          <div className="flex items-center gap-[2px]">
            <Progress
              className="flex-1"
              percent={fileState?.progress}
              showInfo={false}
              strokeColor="#52c41a"
              size="small"
            />
            <div className="text-[grey] text-[10px]">{progressRate}</div>
          </div>
        )}
      </div>
    </>
  );
};

export { VersionPreviewFile };
